import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post } from '../../api/client';

/** A download an admin started (see apps/api/src/powersensor/index.ts). */
export interface FetchRun {
  kind: 'driver' | 'all';
  label: string;
  from: string;
  to: string;
  startedBy: string;
  startedAt: string;
  finishedAt: string | null;
  done: number;
  total: number;
  accounts: number;
  readings: number;
  failed: string[];
  error: string | null;
}

export interface PowersensorStatus {
  configured: boolean;
  running: boolean;
  run: FetchRun | null;
}

export interface DriverPowersensor {
  email: string;
  lastReadingAt: string | null;
  last7Days: number;
}

/** Whether Powersensor is set up, and the latest manual download. Checks every 2 seconds while one runs. */
export function usePowersensorStatus(enabled = true) {
  return useQuery({
    queryKey: ['powersensor'],
    enabled,
    queryFn: () => get<PowersensorStatus>('/powersensor'),
    refetchInterval: (q) => (q.state.data?.running ? 2000 : false),
  });
}

export function useDriverPowersensor(driverId: string, refreshKey: string | null, enabled = true) {
  return useQuery({
    queryKey: ['powersensor', 'driver', driverId, refreshKey],
    enabled,
    queryFn: () => get<DriverPowersensor>(`/powersensor/drivers/${driverId}`),
  });
}

/** Home charging per month (after the threshold), for the chart on Home. */
export function useHomeChargingMonths() {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Australia/Sydney';
  return useQuery({
    queryKey: ['powersensor', 'months', tz],
    queryFn: () => get<{ months: { month: string; kwh: number }[] }>(`/powersensor/months?tz=${encodeURIComponent(tz)}`),
    staleTime: 5 * 60_000,
  });
}

export function useStartFetch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { from: string; to: string; driverId?: string }) => post<PowersensorStatus>('/powersensor/fetch', body),
    onSuccess: (status) => qc.setQueryData(['powersensor'], status),
  });
}
